import { useEffect, useRef, useState } from 'react';
import {
  useApps,
  useDeployments,
  useDispatchGithubDeployments,
  usePlanResults,
  useSetVerificationStatus,
  useUpdateApp,
  useVerificationResults,
} from '../api/hooks';
import { AppPipeline } from '../components/AppPipeline';
import { DeploymentQueueTray } from '../components/DeploymentQueueTray';
import { DeploymentVersionWizard } from '../components/DeploymentVersionWizard';
import { config } from '../config';
import {
  clearGithubDeploymentAuthDraft,
  createGithubAuthorizationState,
  getGithubAuthorizationCallbackParams,
  loadGithubDeploymentAuthDraft,
  removeGithubAuthorizationCallbackParamsFromUrl,
  saveGithubDeploymentAuthDraft,
} from '../lib/githubDeploymentAuth';
import { computePipelineStates } from '../lib/pipelineState';
import { compareVersionTags } from '../lib/version';
import type { Deployment, DeploymentQueueItem, GithubDeploymentAuthItem } from '../types';

export const HomePage = () => {
  const [deploymentQueue, setDeploymentQueue] = useState<DeploymentQueueItem[]>([]);
  const [versionWizardOpen, setVersionWizardOpen] = useState(false);
  const [versionWizardInitialStep, setVersionWizardInitialStep] = useState<'versions' | 'review'>(
    'versions',
  );
  const [githubAuthError, setGithubAuthError] = useState<string>();
  const [highlightedAppName, setHighlightedAppName] = useState<string>();
  const appPipelineRefs = useRef(new Map<string, HTMLDivElement>());
  const highlightFadeTimeoutRef = useRef<number>();
  const scrollFallbackTimeoutRef = useRef<number>();
  const scrollArrivalFrameRef = useRef<number>();
  const scrollEndCleanupRef = useRef<(() => void) | undefined>();
  const githubAuthorizationCallbackHandledRef = useRef(false);
  const { data: apps, isLoading: appsLoading } = useApps();
  const { data: deployments, isLoading: deploymentsLoading } = useDeployments();
  const { data: planResults, isLoading: planResultsLoading } = usePlanResults();
  const { data: verificationResults, isLoading: verificationsLoading } = useVerificationResults();
  const setVerificationStatus = useSetVerificationStatus();
  const updateApp = useUpdateApp();
  const dispatchGithubDeployments = useDispatchGithubDeployments();

  useEffect(() => {
    return () => {
      if (highlightFadeTimeoutRef.current) {
        window.clearTimeout(highlightFadeTimeoutRef.current);
      }

      if (scrollFallbackTimeoutRef.current) {
        window.clearTimeout(scrollFallbackTimeoutRef.current);
      }

      if (scrollArrivalFrameRef.current) {
        window.cancelAnimationFrame(scrollArrivalFrameRef.current);
      }

      scrollEndCleanupRef.current?.();
    };
  }, []);

  useEffect(() => {
    if (githubAuthorizationCallbackHandledRef.current) {
      return;
    }

    const callbackParams = getGithubAuthorizationCallbackParams();

    if (!callbackParams) {
      return;
    }

    githubAuthorizationCallbackHandledRef.current = true;
    removeGithubAuthorizationCallbackParamsFromUrl();

    const draft = loadGithubDeploymentAuthDraft();

    if (!draft || draft.state !== callbackParams.state) {
      setGithubAuthError('GitHub authentication state did not match this deployment session.');
      setVersionWizardInitialStep('review');
      setVersionWizardOpen(true);
      return;
    }

    setGithubAuthError(undefined);
    setDeploymentQueue(
      draft.deployments.map((deployment) => ({
        appName: deployment.appName,
        environment: deployment.environment,
        version: deployment.version,
      })),
    );
    setVersionWizardInitialStep('review');
    setVersionWizardOpen(true);

    dispatchGithubDeployments.mutate(
      {
        code: callbackParams.code,
        state: callbackParams.state,
        redirectUri: draft.redirectUri,
        deployments: draft.deployments,
      },
      {
        onSuccess: () => {
          clearGithubDeploymentAuthDraft();
        },
        onError: (error) => {
          setGithubAuthError(
            error instanceof Error
              ? error.message
              : 'GitHub authentication could not be validated.',
          );
        },
      },
    );
  }, [dispatchGithubDeployments]);

  const isLoading = appsLoading || deploymentsLoading || planResultsLoading || verificationsLoading;

  if (isLoading) {
    return (
      <div className='flex items-center justify-center p-12'>
        <p className='text-muted-foreground'>Loading deployment pipelines...</p>
      </div>
    );
  }

  if (!apps || !deployments || !planResults || !verificationResults) {
    return (
      <div className='flex items-center justify-center p-12'>
        <p className='text-destructive'>Failed to load data</p>
      </div>
    );
  }

  const pipelineStates = computePipelineStates(apps, deployments, planResults, verificationResults);

  const handleValidate = async (deploymentId: number) => {
    await setVerificationStatus.mutateAsync(deploymentId);
  };

  const handleUpdateApp = async (
    appName: string,
    environments: string[],
    validationInstructions?: Record<string, { info?: string }>,
    bulkDeploymentExclusions?: string[],
  ) => {
    await updateApp.mutateAsync({
      appName,
      environments,
      validationInstructions,
      bulkDeploymentExclusions,
    });
  };

  const handleEnqueueDeployment = (item: DeploymentQueueItem) => {
    setDeploymentQueue((current) => {
      const alreadyQueued = current.some(
        (queuedItem) =>
          queuedItem.appName === item.appName && queuedItem.environment === item.environment,
      );

      if (alreadyQueued) {
        return current;
      }

      return [...current, item];
    });
  };

  const handleEnqueueDeployments = (items: DeploymentQueueItem[]) => {
    setDeploymentQueue((current) => {
      const nextQueue = [...current];

      items.forEach((item) => {
        const alreadyQueued = nextQueue.some(
          (queuedItem) =>
            queuedItem.appName === item.appName && queuedItem.environment === item.environment,
        );

        if (!alreadyQueued) {
          nextQueue.push(item);
        }
      });

      return nextQueue;
    });
  };

  const handleRemoveQueuedDeployment = (appName: string, environment: string) => {
    setDeploymentQueue((current) =>
      current.filter((item) => item.appName !== appName || item.environment !== environment),
    );
  };

  const handleContinueToVersionWizard = () => {
    setGithubAuthError(undefined);
    dispatchGithubDeployments.reset();
    setVersionWizardInitialStep('versions');
    setVersionWizardOpen(true);
  };

  const handleStartGithubAuth = (deploymentsToAuthenticate: GithubDeploymentAuthItem[]) => {
    if (!config.github.appClientId) {
      setGithubAuthError('GitHub App client id is not configured.');
      return;
    }

    const state = createGithubAuthorizationState();
    const redirectUri = `${window.location.origin}${window.location.pathname}`;

    saveGithubDeploymentAuthDraft({
      state,
      redirectUri,
      deployments: deploymentsToAuthenticate,
      createdAt: new Date().toISOString(),
    });

    const authorizationUrl = new URL('https://github.com/login/oauth/authorize');
    authorizationUrl.searchParams.set('client_id', config.github.appClientId);
    authorizationUrl.searchParams.set('redirect_uri', redirectUri);
    authorizationUrl.searchParams.set('state', state);

    window.location.assign(authorizationUrl.toString());
  };

  const setAppPipelineRef = (appName: string, element: HTMLDivElement | null) => {
    if (element) {
      appPipelineRefs.current.set(appName, element);
      return;
    }

    appPipelineRefs.current.delete(appName);
  };

  const handleScrollToApp = (appName: string) => {
    const targetElement = appPipelineRefs.current.get(appName);

    if (!targetElement) {
      return;
    }

    if (highlightFadeTimeoutRef.current) {
      window.clearTimeout(highlightFadeTimeoutRef.current);
    }

    if (scrollFallbackTimeoutRef.current) {
      window.clearTimeout(scrollFallbackTimeoutRef.current);
    }

    if (scrollArrivalFrameRef.current) {
      window.cancelAnimationFrame(scrollArrivalFrameRef.current);
    }

    scrollEndCleanupRef.current?.();

    const scrollDistance = Math.abs(
      targetElement.getBoundingClientRect().top - window.innerHeight / 2,
    );
    const fallbackDelay = Math.min(1000, Math.max(350, scrollDistance * 0.45));
    let fadeQueued = false;

    const queueFade = () => {
      if (fadeQueued) {
        return;
      }

      fadeQueued = true;
      scrollEndCleanupRef.current?.();
      if (scrollArrivalFrameRef.current) {
        window.cancelAnimationFrame(scrollArrivalFrameRef.current);
      }
      highlightFadeTimeoutRef.current = window.setTimeout(() => {
        setHighlightedAppName(undefined);
      }, 300);
    };

    const watchForArrival = () => {
      const rect = targetElement.getBoundingClientRect();
      const targetCenter = rect.top + rect.height / 2;
      const viewportCenter = window.innerHeight / 2;
      const arrivalBand = Math.max(80, Math.min(180, rect.height));

      if (Math.abs(targetCenter - viewportCenter) <= arrivalBand) {
        queueFade();
        return;
      }

      scrollArrivalFrameRef.current = window.requestAnimationFrame(watchForArrival);
    };

    setHighlightedAppName(appName);

    window.addEventListener('scrollend', queueFade, { once: true });
    scrollEndCleanupRef.current = () => {
      window.removeEventListener('scrollend', queueFade);
      scrollEndCleanupRef.current = undefined;
    };

    targetElement.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    });

    scrollArrivalFrameRef.current = window.requestAnimationFrame(watchForArrival);
    scrollFallbackTimeoutRef.current = window.setTimeout(queueFade, fallbackDelay);
  };

  const getLatestDeployment = (appName: string, environment: string): Deployment | undefined =>
    deployments
      .filter(
        (deployment) => deployment.appName === appName && deployment.environment === environment,
      )
      .sort(
        (left, right) => new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime(),
      )[0];

  const isQueued = (appName: string, environment: string) =>
    deploymentQueue.some((item) => item.appName === appName && item.environment === environment);

  const isBulkDeploymentExcluded = (appName: string, environment: string) =>
    apps.find((app) => app.name === appName)?.bulkDeploymentExclusions.includes(environment) ??
    false;

  const devDriftCandidates = pipelineStates
    .filter((pipeline) => {
      const app = apps.find((candidateApp) => candidateApp.name === pipeline.appName);
      const enabledEnvironments =
        app?.environments
          .split(',')
          .map((environment) => environment.trim())
          .filter(Boolean) ?? [];
      const devDeployment = getLatestDeployment(pipeline.appName, 'dev');
      const prodDeployment = getLatestDeployment(pipeline.appName, 'prod');
      const devProdComparison =
        devDeployment && prodDeployment
          ? compareVersionTags(devDeployment.version, prodDeployment.version)
          : undefined;

      return (
        pipeline.prodDeployment.hasChanges &&
        enabledEnvironments.includes('dev') &&
        !isBulkDeploymentExcluded(pipeline.appName, 'dev') &&
        !isQueued(pipeline.appName, 'dev') &&
        (!devDeployment ||
          !prodDeployment ||
          (devProdComparison !== undefined && devProdComparison <= 0))
      );
    })
    .map((pipeline) => ({
      appName: pipeline.appName,
      environment: 'dev',
    }));

  const prodDriftCandidates = pipelineStates
    .filter((pipeline) => {
      const app = apps.find((candidateApp) => candidateApp.name === pipeline.appName);
      const enabledEnvironments =
        app?.environments
          .split(',')
          .map((environment) => environment.trim())
          .filter(Boolean) ?? [];
      const devEnabled = enabledEnvironments.includes('dev');
      const devDeployment = getLatestDeployment(pipeline.appName, 'dev');
      const prodDeployment = getLatestDeployment(pipeline.appName, 'prod');
      const devProdComparison =
        devDeployment && prodDeployment
          ? compareVersionTags(devDeployment.version, prodDeployment.version)
          : undefined;

      const devIsReadyForProd =
        !devEnabled ||
        (!!devDeployment &&
          !!prodDeployment &&
          devProdComparison !== undefined &&
          devProdComparison > 0 &&
          devDeployment.status !== 'pending' &&
          devDeployment.status !== 'in_progress' &&
          devDeployment.status !== 'failure' &&
          devDeployment.status !== 'error' &&
          pipeline.devValidation.status === 'success');

      return (
        pipeline.prodDeployment.hasChanges &&
        enabledEnvironments.includes('prod') &&
        !isBulkDeploymentExcluded(pipeline.appName, 'prod') &&
        devIsReadyForProd &&
        !isQueued(pipeline.appName, 'prod')
      );
    })
    .map((pipeline) => ({
      appName: pipeline.appName,
      environment: 'prod',
    }));

  return (
    <div className='space-y-6'>
      {pipelineStates.length === 0 ? (
        <div className='rounded-lg border border-dashed p-12 text-center'>
          <p className='text-muted-foreground'>No infrastructure apps found</p>
        </div>
      ) : (
        <div className='space-y-4'>
          {pipelineStates.map((pipeline) => {
            // Find LATEST deployment IDs for this app (must match the logic in pipelineState.ts)
            const devDeployment = deployments
              .filter((d) => d.appName === pipeline.appName && d.environment === 'dev')
              .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];
            const prodDeployment = deployments
              .filter((d) => d.appName === pipeline.appName && d.environment === 'prod')
              .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];
            const app = apps.find((a) => a.name === pipeline.appName);

            if (!app) {
              return null;
            }

            return (
              <div
                key={pipeline.appName}
                ref={(element) => setAppPipelineRef(pipeline.appName, element)}
                className={`deployment-target-highlight rounded-lg ${
                  highlightedAppName === pipeline.appName
                    ? 'deployment-target-highlight-active'
                    : ''
                }`}
              >
                <AppPipeline
                  pipeline={pipeline}
                  app={app}
                  devDeploymentId={devDeployment?.id}
                  prodDeploymentId={prodDeployment?.id}
                  onValidate={handleValidate}
                  onUpdateApp={handleUpdateApp}
                  onEnqueueDeployment={handleEnqueueDeployment}
                  onDequeueDeployment={handleRemoveQueuedDeployment}
                  queuedDeployments={deploymentQueue}
                  deployments={deployments}
                  planResults={planResults}
                  verificationResults={verificationResults}
                />
              </div>
            );
          })}
        </div>
      )}
      <DeploymentQueueTray
        items={deploymentQueue}
        apps={apps}
        deployments={deployments}
        bulkActions={[
          {
            label: 'Dev',
            count: devDriftCandidates.length,
            onClick: () => handleEnqueueDeployments(devDriftCandidates),
          },
          {
            label: 'Prod',
            count: prodDriftCandidates.length,
            onClick: () => handleEnqueueDeployments(prodDriftCandidates),
          },
        ]}
        onItemClick={handleScrollToApp}
        onRemove={handleRemoveQueuedDeployment}
        onClear={() => setDeploymentQueue([])}
        onContinue={handleContinueToVersionWizard}
      />
      <DeploymentVersionWizard
        open={versionWizardOpen}
        items={deploymentQueue}
        apps={apps}
        deployments={deployments}
        verificationResults={verificationResults}
        initialStep={versionWizardInitialStep}
        githubAuthStatus={
          dispatchGithubDeployments.isPending
            ? 'validating'
            : dispatchGithubDeployments.isSuccess
              ? 'validated'
              : dispatchGithubDeployments.isError || githubAuthError
                ? 'error'
                : 'idle'
        }
        githubAuthResult={dispatchGithubDeployments.data}
        githubAuthError={githubAuthError}
        onRemove={handleRemoveQueuedDeployment}
        onStartGithubAuth={handleStartGithubAuth}
        onOpenChange={setVersionWizardOpen}
      />
    </div>
  );
};
